import EmptyState from 'components/EmptyState'
import Loading from 'components/Loading'
import PageTitle from 'components/PageTitle'
import useQueryString from 'hooks/useQueryString'
import Button from 'new-components/Button'
import Modal from 'new-components/Modal'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import api from 'services/api'
import { PATH_SELECTOR_ICONS } from '../../template/AccountSidePanel/constants/icons'
import { RECEIPT_TYPE_LABELS, buildReceiptDetails } from './helpers'
import * as Styles from './styles'
import { FetchReceiptsResponse, Receipt } from './types'

export default function Receipts() {
  const { filters } = useQueryString()
  const [loading, setLoading] = useState(false)
  const [receipts, setReceipts] = useState<Receipt[]>([])
  const [receiptToPreview, setReceiptToPreview] = useState<Receipt>()


  const highlightedOrderId = filters.pedido

  const visibleReceipts = useMemo(() => {
    if (!highlightedOrderId) return receipts

    const filtered = receipts.filter((receipt) => String(receipt.order_id) === String(highlightedOrderId))

    return filtered.length > 0 ? filtered : receipts
  }, [receipts, highlightedOrderId])

  const isFiltered = visibleReceipts.length !== receipts.length

  const fetchReceipts = async () => {
    setLoading(true)
    try {
      const response = await api.get<FetchReceiptsResponse>('/my-account/receipts')
      setReceipts(response.data.rows)
    } catch (err) {
      console.error(err)
      toast.error('Houve um erro ao recuperar seus comprovantes. Tente novamente.')
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchReceipts()
  }, [])

  return (
    <Styles.ReceiptsContainer>
      <PageTitle>{PATH_SELECTOR_ICONS.prescription} Comprovantes</PageTitle>
      <Styles.Description>
        Comprovantes de pagamento e de estorno dos seus pedidos. Você pode visualizá-los aqui ou baixar o PDF.
      </Styles.Description>

      {isFiltered && (
        <Styles.FilterNotice>
          Mostrando apenas o pedido #{highlightedOrderId}.{' '}
          <a href="/conta?pagina=perfil-comprovantes">Ver todos os comprovantes</a>
        </Styles.FilterNotice>
      )}

      {!loading && receipts.length === 0 && (
        <EmptyState
          title="Nenhum comprovante disponível"
          subTitle="Os comprovantes aparecem aqui depois que o pagamento ou o estorno do pedido é concluído."
        />
      )}

      <Styles.ReceiptsList>
        {visibleReceipts.map((receipt) => (
          <Styles.ReceiptCard key={receipt.id}>
            <Styles.ReceiptInfo>
              <Styles.TypeBadge type={receipt.type}>{RECEIPT_TYPE_LABELS[receipt.type]}</Styles.TypeBadge>
              <strong>Pedido #{receipt.order_id}</strong>
              <span>{buildReceiptDetails(receipt)}</span>
            </Styles.ReceiptInfo>

            <Styles.ReceiptActions>
              <Button background="green" isOutlined onClick={() => setReceiptToPreview(receipt)}>
                Visualizar
              </Button>
              <Styles.DownloadLink href={receipt.download_url} target="_blank" rel="noreferrer">
                Baixar PDF
              </Styles.DownloadLink>
            </Styles.ReceiptActions>
          </Styles.ReceiptCard>
        ))}
      </Styles.ReceiptsList>

      {!!receiptToPreview && (
        <Modal
          isOpen={!!receiptToPreview}
          setOpenModal={() => setReceiptToPreview(undefined)}
          title={`${RECEIPT_TYPE_LABELS[receiptToPreview.type]} - Pedido #${receiptToPreview.order_id}`}>
          <Styles.PreviewFrame src={receiptToPreview.url} title={receiptToPreview.file_name} />
        </Modal>
      )}

      <Loading loading={loading} />
    </Styles.ReceiptsContainer>
  )
}
